/**
 * sl-hle4.13: Per-custom-app multi-locale metadata + AI translation.
 *
 * Drives per-locale title/description editing for one custom app at a
 * time. Persistence routes through the existing
 * `updateCustomAppSEOMutation` (titlesI18n + descriptionsI18n maps —
 * sl-hle4.5). Translation uses `translateCustomAppMetadataMutation`
 * (sl-hle4.7) which charges credits (returned via `microCreditsCharged`,
 * sl-hle4 cluster C+J).
 *
 * Locale set is sourced from `useLocaleSettings` (deployment-driven, not
 * hard-coded). Source-locale picker defaults to the deployment's
 * `defaultLocale`. Bulk-translate runs `overrideExisting: false` so
 * already-filled targets are skipped.
 *
 * SOLID:
 *   - S: One section, multi-locale metadata only. SEO/AIEO concerns live
 *     in their own files.
 *   - D: GraphQLService + WorkspaceService + LocaleSettings — never
 *     touches translation backend directly.
 */
import {
  Button,
  Input,
  Menu,
  MenuItem,
  notify,
  Skeleton,
} from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { useLocaleSettings } from '@affine/core/hooks/use-locale-settings';
import { GraphQLService } from '@affine/core/modules/cloud';
import { GuardService } from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import {
  customAppSeoQuery,
  customAppsQuery,
  TranslatableMetadataField,
  translateCustomAppMetadataMutation,
  updateCustomAppSeoMutation,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

interface ViewListItem {
  id: string;
  title: string | null;
  slug: string;
  isPublished: boolean;
}

interface LocaleMetadata {
  titles: Record<string, string>;
  descriptions: Record<string, string>;
  slugs: Record<string, string>;
}

const EMPTY_METADATA: LocaleMetadata = {
  titles: {},
  descriptions: {},
  slugs: {},
};

function metadataEqual(a: LocaleMetadata, b: LocaleMetadata): boolean {
  return (
    JSON.stringify(a.titles) === JSON.stringify(b.titles) &&
    JSON.stringify(a.descriptions) === JSON.stringify(b.descriptions) &&
    JSON.stringify(a.slugs) === JSON.stringify(b.slugs)
  );
}

export const LocaleSection: FC = () => {
  const t = useI18n();
  const graphqlService = useService(GraphQLService);
  const workspaceService = useService(WorkspaceService);
  const guardService = useService(GuardService);
  const workspaceId = workspaceService.workspace.id;
  const information = useWorkspaceInfo(workspaceService.workspace);
  const canEditCustomApps = useLiveData(
    guardService.can$('Workspace_CustomApps_Edit')
  );
  const isAdmin = !!(
    information?.isOwner ||
    information?.isAdmin ||
    canEditCustomApps === true
  );
  const localeSettings = useLocaleSettings();

  const [views, setViews] = useState<ViewListItem[] | null>(null);
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const [original, setOriginal] = useState<LocaleMetadata | null>(null);
  const [draft, setDraft] = useState<LocaleMetadata | null>(null);
  const [activeLocale, setActiveLocale] = useState<string | null>(null);
  const [sourceLocale, setSourceLocale] = useState<string | null>(null);
  const [viewsError, setViewsError] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [isLoadingViews, setIsLoadingViews] = useState(true);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState<string | null>(null);
  const [isBulkTranslating, setIsBulkTranslating] = useState(false);

  const defaultLocale = localeSettings.settings?.defaultLocale ?? 'en';
  const availableLocales = useMemo(() => {
    const raw = localeSettings.settings?.availableLocales ?? [];
    if (!raw.includes(defaultLocale)) return raw;
    return [defaultLocale, ...raw.filter(l => l !== defaultLocale)];
  }, [localeSettings.settings, defaultLocale]);

  useEffect(() => {
    if (!sourceLocale && defaultLocale) {
      setSourceLocale(defaultLocale);
    }
  }, [sourceLocale, defaultLocale]);

  const loadViews = useAsyncCallback(async () => {
    setIsLoadingViews(true);
    setViewsError(null);
    try {
      const result = await graphqlService.gql({
        query: customAppsQuery,
        variables: { workspaceId },
      });
      const list = result.customApps
        .filter(v => v.isPublished)
        .map(v => ({
          id: v.id,
          title: v.title,
          slug: v.slug,
          isPublished: v.isPublished,
        }));
      setViews(list);
      if (list.length > 0 && !selectedViewId) {
        setSelectedViewId(list[0].id);
      } else if (
        list.length > 0 &&
        selectedViewId &&
        !list.some(v => v.id === selectedViewId)
      ) {
        setSelectedViewId(list[0].id);
      } else if (list.length === 0) {
        setSelectedViewId(null);
      }
    } catch (err) {
      const friendly = UserFriendlyError.fromAny(err);
      setViewsError(friendly.message ?? 'Failed to load custom apps');
      setViews([]);
    } finally {
      setIsLoadingViews(false);
    }
  }, [graphqlService, workspaceId, selectedViewId]);

  useEffect(() => {
    loadViews();
  }, [loadViews]);

  const loadMetadata = useAsyncCallback(
    async (customAppId: string) => {
      setIsLoadingSettings(true);
      setSettingsError(null);
      try {
        const result = await graphqlService.gql({
          query: customAppSeoQuery,
          variables: { customAppId, workspaceId },
        });
        const seo = result.customAppSEO;
        if (!seo) {
          setOriginal({ ...EMPTY_METADATA });
          setDraft({ ...EMPTY_METADATA });
          return;
        }
        const titles = (seo.titlesI18n ?? {}) as Record<string, string>;
        const descriptions = (seo.descriptionsI18n ?? {}) as Record<
          string,
          string
        >;
        const slugs = (seo.slugsI18n ?? {}) as Record<string, string>;
        const next: LocaleMetadata = { titles, descriptions, slugs };
        setOriginal(next);
        setDraft({
          titles: { ...titles },
          descriptions: { ...descriptions },
          slugs: { ...slugs },
        });
        if (!activeLocale) {
          setActiveLocale(defaultLocale);
        }
      } catch (err) {
        const friendly = UserFriendlyError.fromAny(err);
        setSettingsError(friendly.message ?? 'Failed to load locale metadata');
        setOriginal(null);
        setDraft(null);
      } finally {
        setIsLoadingSettings(false);
      }
    },
    [graphqlService, workspaceId, activeLocale, defaultLocale]
  );

  useEffect(() => {
    if (selectedViewId) {
      loadMetadata(selectedViewId);
    }
  }, [selectedViewId, loadMetadata]);

  const dirty = useMemo(
    () => !!(original && draft && !metadataEqual(original, draft)),
    [original, draft]
  );

  const enabledLocales = useMemo(() => {
    if (!draft) return [] as string[];
    const set = new Set<string>([
      ...Object.keys(draft.titles),
      ...Object.keys(draft.descriptions),
    ]);
    if (sourceLocale) set.add(sourceLocale);
    return Array.from(set).sort();
  }, [draft, sourceLocale]);

  const setTitle = useCallback((locale: string, value: string) => {
    setDraft(prev => {
      if (!prev) return prev;
      return { ...prev, titles: { ...prev.titles, [locale]: value } };
    });
  }, []);

  const setDescription = useCallback((locale: string, value: string) => {
    setDraft(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        descriptions: { ...prev.descriptions, [locale]: value },
      };
    });
  }, []);

  const setSlug = useCallback((locale: string, value: string) => {
    setDraft(prev => {
      if (!prev) return prev;
      return { ...prev, slugs: { ...prev.slugs, [locale]: value } };
    });
  }, []);

  const ensureLocale = useCallback((locale: string) => {
    setDraft(prev => {
      if (!prev) return prev;
      if (
        locale in prev.titles ||
        locale in prev.descriptions ||
        locale in prev.slugs
      ) {
        return prev;
      }
      return {
        titles: { ...prev.titles, [locale]: '' },
        descriptions: { ...prev.descriptions, [locale]: '' },
        slugs: { ...prev.slugs, [locale]: '' },
      };
    });
    setActiveLocale(locale);
  }, []);

  const removeLocale = useCallback(
    (locale: string) => {
      if (locale === sourceLocale) return;
      setDraft(prev => {
        if (!prev) return prev;
        const titles = { ...prev.titles };
        const descriptions = { ...prev.descriptions };
        const slugs = { ...prev.slugs };
        delete titles[locale];
        delete descriptions[locale];
        delete slugs[locale];
        return { titles, descriptions, slugs };
      });
      if (activeLocale === locale) {
        setActiveLocale(sourceLocale);
      }
    },
    [activeLocale, sourceLocale]
  );

  const handleSave = useAsyncCallback(async () => {
    if (!isAdmin || !draft || !selectedViewId) return;
    setIsSaving(true);
    try {
      await graphqlService.gql({
        query: updateCustomAppSeoMutation,
        variables: {
          input: {
            customAppId: selectedViewId,
            workspaceId,
            titlesI18n: draft.titles,
            descriptionsI18n: draft.descriptions,
            slugsI18n: draft.slugs,
          },
        },
      });
      notify.success({
        title:
          t['com.streamlain.settings.workspace.customApps.locale.savedToast'](),
      });
      loadMetadata(selectedViewId);
    } catch (err) {
      notify.error({
        title:
          t['com.streamlain.settings.workspace.customApps.locale.saveFailed'](),
        message: UserFriendlyError.fromAny(err).message ?? String(err),
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    isAdmin,
    draft,
    selectedViewId,
    workspaceId,
    graphqlService,
    loadMetadata,
    t,
  ]);

  const runTranslate = useCallback(
    async (targetLocales: string[], overrideExisting: boolean) => {
      if (!sourceLocale || !selectedViewId) return;
      const filtered = targetLocales.filter(loc => loc !== sourceLocale);
      if (filtered.length === 0) return;
      const fieldCount = 3;
      const maxOperations = filtered.length * fieldCount;
      if (
        !window.confirm(
          t[
            'com.streamlain.settings.workspace.customApps.locale.translateConfirm'
          ]({
            count: String(maxOperations),
            from: sourceLocale,
            to: filtered.join(', '),
          })
        )
      ) {
        return;
      }
      try {
        const result = await graphqlService.gql({
          query: translateCustomAppMetadataMutation,
          variables: {
            input: {
              customAppId: selectedViewId,
              workspaceId,
              sourceLocale,
              targetLocales: filtered,
              fields: [
                TranslatableMetadataField.TITLE,
                TranslatableMetadataField.DESCRIPTION,
                TranslatableMetadataField.SLUG,
              ],
              overrideExisting,
            },
          },
        });
        const out = result.translateCustomAppMetadata;
        const microCharged = BigInt(out.microCreditsCharged ?? '0');
        const credits = Number(microCharged) / 10_000;
        notify.success({
          title:
            t[
              'com.streamlain.settings.workspace.customApps.locale.translatedToast'
            ]?.({
              count: String(out.translatedLocales.length),
              credits: credits.toFixed(2),
            }) ??
            `Translated ${out.translatedLocales.length} locales · ${credits.toFixed(
              2
            )} credits`,
        });
        loadMetadata(selectedViewId);
      } catch (err) {
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.customApps.locale.translateFailed'
            ](),
          message: UserFriendlyError.fromAny(err).message ?? String(err),
        });
      }
    },
    [graphqlService, workspaceId, selectedViewId, sourceLocale, loadMetadata, t]
  );

  const handleTranslateLocale = useAsyncCallback(
    async (target: string) => {
      setIsTranslating(target);
      try {
        await runTranslate([target], false);
      } finally {
        setIsTranslating(null);
      }
    },
    [runTranslate]
  );

  const handleBulkTranslate = useAsyncCallback(async () => {
    setIsBulkTranslating(true);
    try {
      await runTranslate([...enabledLocales], false);
    } finally {
      setIsBulkTranslating(false);
    }
  }, [enabledLocales, runTranslate]);

  const sectionTitle =
    t['com.streamlain.settings.workspace.customApps.locale.title']();

  if (isLoadingViews || localeSettings.isLoading) {
    return (
      <SettingWrapper title={sectionTitle}>
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.locale.loading'
          ]()}
          desc=""
        />
      </SettingWrapper>
    );
  }

  if (viewsError) {
    return (
      <SettingWrapper title={sectionTitle}>
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.locale.viewsError'
          ]()}
          desc={viewsError}
        />
      </SettingWrapper>
    );
  }

  if (!views || views.length === 0) {
    return (
      <SettingWrapper title={sectionTitle}>
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.locale.empty.title'
          ]()}
          desc={t[
            'com.streamlain.settings.workspace.customApps.locale.empty.desc'
          ]()}
        />
      </SettingWrapper>
    );
  }

  const selectedView = views.find(v => v.id === selectedViewId) ?? null;
  const showSelector = views.length > 1;
  const tabLocale = activeLocale ?? sourceLocale ?? defaultLocale;

  return (
    <SettingWrapper title={sectionTitle}>
      {showSelector ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.locale.selector.label'
          ]()}
          desc={t[
            'com.streamlain.settings.workspace.customApps.locale.selector.desc'
          ]()}
        >
          <Menu
            items={views.map(view => (
              <MenuItem
                key={view.id}
                onClick={() => setSelectedViewId(view.id)}
                selected={view.id === selectedViewId}
              >
                {view.title?.length ? view.title : view.slug}
              </MenuItem>
            ))}
          >
            <Button>
              {selectedView?.title?.length
                ? selectedView.title
                : (selectedView?.slug ?? '—')}
            </Button>
          </Menu>
        </SettingRow>
      ) : null}

      {isLoadingSettings ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.locale.loadingSettings'
          ]()}
          desc=""
        />
      ) : settingsError ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.locale.loadFailed'
          ]()}
          desc={settingsError}
        >
          <Button
            variant="secondary"
            onClick={() => selectedViewId && loadMetadata(selectedViewId)}
          >
            {t['com.streamlain.settings.workspace.customApps.locale.retry']()}
          </Button>
        </SettingRow>
      ) : !draft ? null : (
        <>
          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.locale.source.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.locale.source.desc'
            ]()}
          >
            <Menu
              items={availableLocales.map(loc => (
                <MenuItem
                  key={loc}
                  onClick={() => setSourceLocale(loc)}
                  selected={loc === sourceLocale}
                >
                  {loc}
                </MenuItem>
              ))}
            >
              <Button disabled={!isAdmin || isSaving}>
                {sourceLocale ?? defaultLocale}
              </Button>
            </Menu>
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.locale.enabled.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.locale.enabled.desc'
            ]()}
            spreadCol={false}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {availableLocales.map(loc => {
                const enabled = enabledLocales.includes(loc);
                const isSource = loc === sourceLocale;
                return (
                  <button
                    key={loc}
                    type="button"
                    disabled={!isAdmin || isSaving}
                    onClick={() => {
                      if (enabled && !isSource) {
                        removeLocale(loc);
                      } else {
                        ensureLocale(loc);
                      }
                    }}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 16,
                      border:
                        '1px solid var(--streamlain-v2-layer-insideBorder-border)',
                      background: enabled
                        ? 'var(--streamlain-v2-layer-background-primary)'
                        : 'var(--streamlain-v2-layer-background-secondary)',
                      color: enabled
                        ? 'var(--streamlain-v2-text-primary)'
                        : 'var(--streamlain-v2-text-secondary)',
                      cursor: isAdmin && !isSaving ? 'pointer' : 'not-allowed',
                      fontSize: 12,
                      opacity: !isAdmin || isSaving ? 0.6 : 1,
                    }}
                  >
                    {loc}
                    {isSource ? ' ★' : ''}
                  </button>
                );
              })}
            </div>
          </SettingRow>

          {enabledLocales.length > 0 ? (
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.locale.tabs.label'
              ]()}
              desc={t[
                'com.streamlain.settings.workspace.customApps.locale.tabs.desc'
              ]()}
              spreadCol={false}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  width: '100%',
                }}
              >
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {enabledLocales.map(loc => (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => setActiveLocale(loc)}
                      data-active={loc === tabLocale || undefined}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 6,
                        border:
                          loc === tabLocale
                            ? '1px solid var(--streamlain-success-color)'
                            : '1px solid var(--streamlain-v2-layer-insideBorder-border)',
                        background:
                          loc === tabLocale
                            ? 'var(--streamlain-v2-layer-background-primary)'
                            : 'transparent',
                        cursor: 'pointer',
                        fontSize: 12,
                      }}
                    >
                      {loc}
                      {loc === sourceLocale ? ' ★' : ''}
                    </button>
                  ))}
                </div>
                {isBulkTranslating || isTranslating === tabLocale ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <Skeleton height={32} />
                    <Skeleton height={72} />
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <Input
                      value={draft.titles[tabLocale] ?? ''}
                      onChange={value => setTitle(tabLocale, value)}
                      disabled={!isAdmin || isSaving}
                      placeholder={t[
                        'com.streamlain.settings.workspace.customApps.locale.title.placeholder'
                      ]()}
                    />
                    <Input
                      value={draft.slugs[tabLocale] ?? ''}
                      onChange={value => setSlug(tabLocale, value)}
                      disabled={!isAdmin || isSaving}
                      placeholder={t[
                        'com.streamlain.settings.workspace.customApps.locale.slug.placeholder'
                      ]()}
                    />
                    <textarea
                      value={draft.descriptions[tabLocale] ?? ''}
                      onChange={e => setDescription(tabLocale, e.target.value)}
                      disabled={!isAdmin || isSaving}
                      rows={3}
                      placeholder={t[
                        'com.streamlain.settings.workspace.customApps.locale.description.placeholder'
                      ]()}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 6,
                        border:
                          '1px solid var(--streamlain-v2-layer-insideBorder-border)',
                        background:
                          'var(--streamlain-v2-layer-background-primary)',
                        color: 'var(--streamlain-v2-text-primary)',
                        fontFamily: 'inherit',
                        fontSize: 14,
                        resize: 'vertical',
                      }}
                    />
                    {tabLocale !== sourceLocale && isAdmin ? (
                      <Button
                        variant="secondary"
                        disabled={
                          isSaving ||
                          isTranslating === tabLocale ||
                          !sourceLocale ||
                          !draft.titles[sourceLocale]
                        }
                        onClick={() => handleTranslateLocale(tabLocale)}
                      >
                        {isTranslating === tabLocale
                          ? t[
                              'com.streamlain.settings.workspace.customApps.locale.translatingAction'
                            ]()
                          : (t[
                              'com.streamlain.settings.workspace.customApps.locale.translateAction'
                            ]?.({
                              from: sourceLocale ?? '',
                              to: tabLocale,
                            }) ??
                            `Translate from ${sourceLocale} to ${tabLocale}`)}
                      </Button>
                    ) : null}
                  </div>
                )}
              </div>
            </SettingRow>
          ) : null}

          {isAdmin ? (
            <SettingRow name="" desc="">
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="secondary"
                  disabled={
                    isBulkTranslating ||
                    isSaving ||
                    !sourceLocale ||
                    enabledLocales.length <= 1
                  }
                  onClick={handleBulkTranslate}
                >
                  {isBulkTranslating
                    ? t[
                        'com.streamlain.settings.workspace.customApps.locale.bulkTranslatingAction'
                      ]()
                    : t[
                        'com.streamlain.settings.workspace.customApps.locale.bulkTranslateAction'
                      ]()}
                </Button>
                <Button
                  variant="primary"
                  disabled={!dirty || isSaving}
                  onClick={handleSave}
                >
                  {isSaving
                    ? t[
                        'com.streamlain.settings.workspace.customApps.locale.savingAction'
                      ]()
                    : t[
                        'com.streamlain.settings.workspace.customApps.locale.saveAction'
                      ]()}
                </Button>
              </div>
            </SettingRow>
          ) : null}
        </>
      )}
    </SettingWrapper>
  );
};
